import { useCallback, useMemo } from 'react';
import { useSession } from './use-session.hook';

/**
 * Permisos del front, espejo de los `@Roles()` del backend raptor-cpd (8 sep 2026):
 *
 * | recurso                              | operador | supervisor | admin | superadmin |
 * |--------------------------------------|----------|------------|-------|------------|
 * | dashboard, reports, lecturas (GET)   |    ✓     |     ✓      |   ✓   |     ✓      |
 * | cpd/* escritura + cpd/reload         |          |     ✓      |   ✓   |     ✓      |
 * | salientes/entrantes/dialplan escrit. |          |     ✓      |   ✓   |     ✓      |
 * | entrantes/reload, dialplan/reload    |          |     ✓      |   ✓   |     ✓      |
 * | salientes/reload (lb_reload)         |          |            |   ✓   |     ✓      |
 * | users/*                              |          |            |   ✓   |     ✓      |
 * | audit                                |          |            |   ✓   |     ✓      |
 *
 * `superadmin` es comodín en el RolesGuard del backend. El rol efectivo viene en
 * `session.user.roles[0]` (respuesta de login). Ocultar en UI es cortesía: el backend valida.
 */

export type Role = 'operador' | 'supervisor' | 'admin' | 'superadmin';

export type ModuleName =
  | 'dashboard'
  | 'reports'
  | 'cpd'
  | 'opensips'
  | 'dialplan'
  | 'users'
  | 'audit';

export type PermissionType = 'read' | 'write';

export type Action =
  | 'cpd.write'
  | 'cpd.reload'
  | 'opensips.write'
  | 'opensips.reload'
  | 'opensips.reload.lb'
  | 'users.write'
  | 'audit.read';

interface ModulePermissions {
  read: boolean;
  write: boolean;
}

const RANK: Record<Role, number> = { operador: 1, supervisor: 2, admin: 3, superadmin: 4 };

/** Rol mínimo para cada acción. */
const ACTION_MIN_ROLE: Record<Action, Role> = {
  'cpd.write': 'supervisor',
  'cpd.reload': 'supervisor',
  'opensips.write': 'supervisor',
  'opensips.reload': 'supervisor',
  'opensips.reload.lb': 'admin',
  'users.write': 'admin',
  'audit.read': 'admin'
};

/** Rol mínimo para leer / escribir cada módulo. */
const MODULE_MIN_ROLE: Record<ModuleName, { read: Role; write: Role }> = {
  dashboard: { read: 'operador', write: 'admin' },
  reports: { read: 'operador', write: 'admin' },
  cpd: { read: 'operador', write: 'supervisor' },
  opensips: { read: 'operador', write: 'supervisor' },
  dialplan: { read: 'operador', write: 'supervisor' },
  users: { read: 'admin', write: 'admin' },
  audit: { read: 'admin', write: 'admin' }
};

function normalizeRole(raw: unknown): Role | null {
  const r = String(raw ?? '').toLowerCase();
  if (r === 'root') return 'superadmin'; // legado del portal viejo
  if (r === 'operator') return 'operador';
  return (Object.keys(RANK) as Role[]).includes(r as Role) ? (r as Role) : null;
}

export const usePermissions = () => {
  const { session } = useSession();

  const role = useMemo<Role | null>(() => {
    const roles: unknown[] = Array.isArray(session?.user?.roles) ? session.user.roles : [];
    // si el usuario tiene varios roles, gana el de mayor rango
    const best = roles
      .map(normalizeRole)
      .filter((r): r is Role => r !== null)
      .sort((a, b) => RANK[b] - RANK[a])[0];
    return best ?? normalizeRole(session?.profile);
  }, [session?.user?.roles, session?.profile]);

  const atLeast = useCallback((min: Role) => (role ? RANK[role] >= RANK[min] : false), [role]);

  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'admin' || isSuperAdmin;
  const isSupervisor = role === 'supervisor';
  const isOperador = role === 'operador';

  const can = useCallback((action: Action) => atLeast(ACTION_MIN_ROLE[action]), [atLeast]);

  const hasPermission = useCallback(
    (module: ModuleName, type: PermissionType): boolean => {
      const rule = MODULE_MIN_ROLE[module];
      if (!rule) return false;
      return atLeast(rule[type]);
    },
    [atLeast]
  );

  const canRead = useCallback(
    (module: ModuleName) => hasPermission(module, 'read'),
    [hasPermission]
  );
  const canWrite = useCallback(
    (module: ModuleName) => hasPermission(module, 'write'),
    [hasPermission]
  );
  const canView = canRead;

  const getModulePermissions = useCallback(
    (module: ModuleName): ModulePermissions => ({ read: canRead(module), write: canWrite(module) }),
    [canRead, canWrite]
  );

  return {
    role,
    isSuperAdmin,
    isAdmin,
    isSupervisor,
    isOperador,
    /** compat: "usuario estándar" = operador (solo lectura) */
    isStandardUser: isOperador,
    /** compat con el portal viejo; aquí solo superadmin */
    hasDataBridgeAccess: isSuperAdmin,
    rolePermissions: null,
    can,
    atLeast,
    hasPermission,
    canRead,
    canWrite,
    canView,
    getModulePermissions
  };
};
